import { useCallback } from "react";

import {
  phoneWithCountryCode,
  useLoginSendSmsCodeMutation,
  useRegisterAccountMutation,
} from "@stepapp/core";
import { captureSentryException } from "@stepapp/sentry";

import { loaderRef } from "../components/Loader";
import { AppStorage } from "../services/AppStorage";

type Params = {
  phoneNumber: string;
};

const useSendSMSCode = () => {
  const [loginSendSmsCode] = useLoginSendSmsCodeMutation();
  const [registerAccount] = useRegisterAccountMutation();

  const handleSMSCodeSend = useCallback(
    async ({ phoneNumber }: Params) => {
      loaderRef.current?.showLoader();

      let response = { success: false } as {
        success: boolean;
        isRegister?: boolean;
        disabled?: boolean;
        error?: string;
      };

      try {
        const number = phoneWithCountryCode(phoneNumber, AppStorage().get("countryCode"));
        const { data, errors } = await loginSendSmsCode({ variables: { phone_number: number } });
        const numberNotFound = errors?.[0]?.message.toLowerCase().includes("not found");

        if (data?.startLoginChallenge) {
          response = { success: !errors };
        }

        if (numberNotFound) {
          await registerAccount({
            variables: { phoneNumber: number },
          });
          const { errors } = await loginSendSmsCode({ variables: { phone_number: number } });
          response = {
            success: !errors,
            isRegister: true,
          };
        } else {
          const isDisabled = errors?.[0]?.message.includes("disabled");
          response = {
            success: !errors,
            isRegister: false,
            disabled: isDisabled,
            error: errors?.[0]?.message,
          };
        }
      } catch (error) {
        captureSentryException("[Send SMS error]", {
          extra: {
            //@ts-expect-error
            errorMessage: error?.message,
          },
        });
        response = { success: false };
      }

      loaderRef.current?.hideLoader();
      return response;
    },
    [loginSendSmsCode, registerAccount],
  );

  return handleSMSCodeSend;
};

export default useSendSMSCode;
